'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowLeft, Mail, MessageCircle, MapPin } from 'lucide-react';

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

export default function ContactClient() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-2xl mx-auto px-6">
          <div className="mb-8">
            <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
              <ArrowLeft size={14} />
              Back to Home
            </Link>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">R3SET Running</p>
            <h1 className="text-3xl font-bold text-foreground mb-2">Contact Us</h1>
            <p className="text-muted-foreground leading-relaxed">
              Have a question, want to join, or just want to say hello? We'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            {/* Contact Details */}
            <div className="space-y-5">
              <div className="p-5 bg-card rounded-xl border border-border shadow-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0">
                    <Mail size={16} className="text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-0.5">General Enquiries</p>
                    <a href="mailto:hello@r3set.run" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                      hello@r3set.run
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-card rounded-xl border border-border shadow-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0">
                    <InstagramIcon />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-0.5">Instagram</p>
                    <a
                      href="https://instagram.com/r3set.run"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      @r3set.run
                    </a>
                    <p className="text-xs text-muted-foreground mt-0.5">Session updates, photos, community stories</p>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-card rounded-xl border border-border shadow-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={16} className="text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-0.5">Community Support</p>
                    <p className="text-sm text-muted-foreground">
                      For member support and community questions, reach us via email or Instagram DM.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-card rounded-xl border border-border shadow-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0">
                    <MapPin size={16} className="text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-0.5">Based In</p>
                    <p className="text-sm text-muted-foreground">Hong Kong</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Sessions across HK Island, Kowloon & NT</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="p-6 bg-card rounded-xl border border-border shadow-card text-center">
                  <div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                    <Mail size={20} className="text-green-600" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">Message Sent</h3>
                  <p className="text-sm text-muted-foreground">
                    Thanks for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', message: '' }); }}
                    className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-6 bg-card rounded-xl border border-border shadow-card space-y-4">
                  <p className="text-sm font-semibold text-foreground">Send a Message</p>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Email <span className="text-sm font-medium text-foreground mb-1.5"><span className="text-red-500">*</span></span>
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="How can we help?"
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center gap-2 py-3 text-sm font-semibold"
                  >
                    <Mail size={15} />
                    Send Message
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    We typically respond within 1–2 business days.
                  </p>
                </form>
              )}
            </div>
          </div>

          <div className="pt-8 border-t border-border flex flex-wrap gap-4 text-sm text-muted-foreground">
            <Link href="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link href="/terms-of-use" className="hover:text-foreground transition-colors">Terms of Use</Link>
            <Link href="/cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
